import {
  Component, ViewChild, Output, EventEmitter, input, computed,
  Input, inject,
} from '@angular/core';
import { Editor, EditorInitEvent, EditorTextChangeEvent } from 'primeng/editor';
import Quill from 'quill';
import { HttpClient } from '@angular/common/http';
import { switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import Table from 'quill/modules/table';
import { ContentsService } from '../../services/contents.service';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';

type Handlers = { [key: string]: () => void };

@Component({
    selector: 'app-editor',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.scss'],
    imports: [Editor, NgClass, FormsModule],
})
export class EditorComponent {
  @ViewChild('editorRef') editorRef!: Editor; // Reference to PrimeNG Editor

  @Input()
    value: unknown;

  @Output() valueChange = new EventEmitter<unknown>();

  inline = input<boolean>(true);

  allowPdf = input<boolean>(false);

  table = input<boolean>(false);

  @Input()
    ngClass: string | string[] | Set<string> | { [p: string]: unknown } | null | undefined;

  editorConfig = input<Record<string, unknown>>();

  protected modules = computed(() => {
    const config = this.editorConfig();
    if (!config || !config['toolbar']) {
      return config;
    }
    const allowPdf = this.allowPdf();
    const inline = this.inline();
    // Allow PDF usage only if it gets uploaded
    const handlers: Handlers = {};
    if (!inline && allowPdf) {
      handlers['pdf'] = () => this.pdf();
    }
    if (!inline) {
      handlers['image'] = () => this.image();
    }
    if (this.table()) {
      config['table'] = true;
      handlers['table_insert'] = () => this.tableInsert();
      handlers['table_append_row'] = () => this.tableAppendRow();
      handlers['table_append_column'] = () => this.tableAppendColumn();
      handlers['table_delete_row'] = () => this.tableDeleteRow();
      handlers['table_delete_column'] = () => this.tableDeleteColumn();
      handlers['table_delete_table'] = () => this.tableDeleteTable();
    }

    if (Object.keys(handlers).length > 0) {
      if (Array.isArray(config['toolbar'])) {
        config['toolbar'] = {
          container: config['toolbar'],
          handlers,
        };
      } else {
        config['toolbar'] = {
          container: (config as { container: Record<string, unknown> }).container,
          handlers,
        };
      }
    }
    return config;
  });

  @Output() contentChange = new EventEmitter<string | null>(); // Emit changes to parent

  quillInstance!: Quill; // Store the Quill instance

  private contentsService = inject(ContentsService);
  private http = inject(HttpClient);

  pdf(): void {
    if (this.inline() || !this.allowPdf()) {
      return;
    }
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.setAttribute('accept', 'application/pdf');
    fileInput.click();

    fileInput.onchange = async () => {
      const file = fileInput?.files?.[0];
      this.runUpload('pdf', file);
    };
  }

  image() {
    if (this.inline()) {
      return;
    }
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.setAttribute('accept', 'image/*');
    fileInput.click();

    fileInput.onchange = async () => {
      const file = fileInput?.files?.[0];
      return this.runUpload('image', file);
    };
  }

  private runUpload(embedType: 'image' | 'pdf', file?: File): void {
    if (!file) {
      return;
    }
    const range = this.quillInstance.getSelection();
    if (!range) {
      return;
    }
    const contentType = file.type;
    const fileName = file.name;
    this.contentsService.getSignedUploadUrl(fileName, contentType).pipe(
      tap(({ key }) => {
        const fullSrc = URL.createObjectURL(file);
        this.quillInstance.insertEmbed(range.index, embedType, { src: fullSrc, key }, 'user');
        this.quillInstance.insertText(range.index + 1, '\n', 'user');
        this.quillInstance.setSelection(range.index + 2, 'user');
      }),
      switchMap(({ url }) => this.http.put(url, file)),
    ).subscribe();
  }

  private tableInsert(): void {
    const range = this.quillInstance.getSelection();
    if (!range) {
      return;
    }
    const table = this.quillInstance.getModule('table') as Table;
    table.insertTable(2, 2);
  }

  private tableAppendRow(): void {
    const range = this.quillInstance.getSelection();
    if (!range) {
      return;
    }
    const table = this.quillInstance.getModule('table') as Table;
    table.insertRowBelow();
  }

  private tableAppendColumn(): void {
    const range = this.quillInstance.getSelection();
    if (!range) {
      return;
    }
    const table = this.quillInstance.getModule('table') as Table;
    table.insertColumnRight();
  }

  private tableDeleteRow(): void {
    const range = this.quillInstance.getSelection();
    if (!range) {
      return;
    }
    const table = this.quillInstance.getModule('table') as Table;
    table.deleteRow();
  }

  private tableDeleteColumn(): void {
    const range = this.quillInstance.getSelection();
    if (!range) {
      return;
    }
    const table = this.quillInstance.getModule('table') as Table;
    table.deleteColumn();
  }

  private tableDeleteTable(): void {
    const range = this.quillInstance.getSelection();
    if (!range) {
      return;
    }
    const table = this.quillInstance.getModule('table') as Table;
    table.deleteTable();
  }

  onEditorCreated(event: EditorInitEvent): void {
    this.quillInstance = event.editor as Quill;
  }

  onContentChanged(event: EditorTextChangeEvent) {
    this.contentChange.emit(event.htmlValue);
    this.valueChange.emit(event.htmlValue);
  }
}
